import { Permission } from '@vitesse/proto/vitesse/v1/entitlements_pb.js';
import { registerClientTools, type ClientTool, type ToolOutcome } from '@vitesse/base-ui/ai';
import { GATEWAY_PARAM, resolveGateway, type GatewayScope } from './gateway-resolve';
import type { LanguageDiagnostic } from '@vitesse/statechart-host';
import { checkDocument, describeDocument, handlesCommand, listFindings, OUTLINE_MAX_LINES, problems, refusalMessage } from './model-check';
import {
  applyWrite,
  checkDraft,
  draftOf,
  emptyDraft,
  groupNote,
  literalFrom,
  optionsOf,
  profileOf,
  runsAsGroup,
  upsertOf,
  type DraftBinding,
  type DraftChild,
  type DraftNotify,
  type DraftPlcOutput,
  type DraftTemplate,
  type DraftWrite,
  type ModelDraft,
} from './model-draft';
import { portTypes } from './port-types';
import type { ModelMachine } from '../../core/domain/model';
import { stripForEngine } from '../../sandbox/statechart/typescript';

/*
 * WHAT THE ASSISTANT MAY ASK ABOUT THIS GATEWAY'S MODELS.
 *
 * The first real client tools on the platform, so a word on why these are client tools rather than
 * RPC annotations on the worker. A model lives on the FABRIC, behind the gateway's own api-gateway,
 * reachable by a browser holding a session for that relay — the AI worker has no route to it and
 * should not grow one. This is precisely the case the client-tool mechanism exists for: the surface
 * that can already do the thing declares that the model may ask for it.
 *
 * WRITING IS THE POINT, not an escalation of it. On this platform the assistant AUTHORS statecharts
 * — that is the product, and the absence of a hand-authoring screen is the design rather than a gap
 * to route around. A person edits afterwards, in the SCXML view, once there is something to edit.
 *
 * TWO GATES, and they are different in kind, which is the thing to keep straight:
 *
 *   · WHAT A PERSON MAY DO is the real boundary, and it is not enforced here. A client tool runs in
 *     this browser on the user's own session, so `Models.Update` is authorized against
 *     `model:edit` exactly as it would be if they had clicked a button. The assistant cannot reach
 *     past its operator: declaring a tool grants the page nothing it did not already have.
 *   · WHAT A SPECIALIST MAY REACH is scope, not security — an expert's `tool_subset` keeps the
 *     SCXML author to the tools it needs. It narrows a capable caller; it never widens one.
 *
 * SAFETY LIVES IN THE MASK. An edit names only the parts it changes, so a rewrite cannot silently
 * drop the connections a person made: the model-runner merges only the fields the write names. A
 * creation sends every part, and it lands STOPPED: a statechart drives a PLC, and a PLC drives
 * something that moves, so nothing this writes starts running because it was written.
 *
 * NOTHING RETURNED HERE IS AN INSTRUCTION. A model's SCXML is authored by customers and is data;
 * comments inside it are page content, not direction.
 */

/** How the tools reach a gateway: `gateway` on each call, resolved against the org's whole roster —
 *  see gateway-resolve.ts for why this replaced a single captured "current" entry. */
export interface ModelToolDeps extends GatewayScope {
  /** The org's tool catalog (AiAdmin/ListTools): models.write finds the Slack server's send_dm in it. Absent: no Slack messages. */
  mcpTools?: () => Promise<{ name: string; tier: number }[]>;
  /** Who each of the org's models acts as (AiAdmin/ListArmings): a Slack message goes out only for an armed model. */
  armings?: () => Promise<{ modelId: string; userName: string }[]>;
}

/*
 * THE ARMING, SAID IN THE REPLY THAT SETS THE SLACK MESSAGE.
 *
 * A model sends with the integrations of the person who armed it, and arming is that person's own
 * click: ArmModel is no AI tool. So the reply tells the assistant who the model acts as, or that
 * nobody does and what the person must do. Without it, each change fails, and only the model log
 * says why. An arming that cannot be read counts as none, as the model page's Acts as section shows it.
 */
async function armingNote(deps: ModelToolDeps, modelId: string): Promise<string> {
  const armings = deps.armings ? await deps.armings().catch(() => []) : [];
  const armed = armings.find((a) => a.modelId === modelId);
  if (armed) return ` It acts as ${armed.userName || 'the person who armed it'}, so the messages go out while it runs.`;
  return (
    ' Nobody has armed it, so no message can go out yet. Tell the person to open the model page and press "Act as me" under Acts as:' +
    ' the model sends with the integrations of the person who arms it, and you cannot arm it for them.'
  );
}

/*
 * THE SLACK SERVER OF THE ORG, FOUND BY ITS TOOL.
 *
 * A person asks for "a Slack message to Colin on every state change". They do not name an MCP
 * server, and its id (`mcp-1a2b3c4d`) is the console's. So the server is the one whose tools include
 * send_dm (workers/slack-mcp-worker), and the descriptor stores the full name that the plane runs.
 *
 * A banned or an Ask-tier tool is refused here: the plane refuses both for an automation, which has
 * nobody to approve a call, and a model that fails at every change is worse than a write that says why.
 */
const SEND_DM = /^mcp__.+__send_dm$/;
const TIER_ASK = 2;
const TIER_BAN = 3;

async function slackToolOf(deps: ModelToolDeps, named: string): Promise<{ ok: true; tool: string } | { ok: false; message: string }> {
  const tools = (deps.mcpTools ? await deps.mcpTools() : []).filter((t) => SEND_DM.test(t.name));
  const chosen = named ? tools.filter((t) => t.name === named) : tools;
  if (chosen.length === 0) {
    return {
      ok: false,
      message: named
        ? `No Slack server of this organisation has the tool "${named}".${tools.length ? ` The ones that send a direct message: ${tools.map((t) => t.name).join(', ')}.` : ''}`
        : 'No Slack server with send_dm is in the AI configuration of this organisation, or the AI plane could not be reached. ' +
          'Nothing was written. Tell the person: an admin must add the Slack MCP server (https://slack-mcp.vitesseautomation.ai/mcp) under MCP Servers first.',
    };
  }
  if (chosen.length > 1) {
    return {
      ok: false,
      message: `More than one Slack server can send a direct message: ${chosen.map((t) => t.name).join(', ')}. Ask the person which one, and give it as slackTool.`,
    };
  }
  const [tool] = chosen;
  if (tool.tier === TIER_BAN)
    return { ok: false, message: `${tool.name} is turned off for this organisation, so no message could go out. An admin can allow it in AI configuration.` };
  if (tool.tier === TIER_ASK)
    return {
      ok: false,
      message: `${tool.name} needs a person's approval for each call, and a model has nobody to give it. An admin must set it to Allow in AI configuration.`,
    };
  return { ok: true, tool: tool.name };
}

/*
 * TAKE THE PERSON TO WHAT WAS JUST WRITTEN.
 *
 * A statechart the assistant authored is invisible until somebody navigates to it, and "I created
 * Racecar" leaves them to find it — on a relay with forty models, from a name. The tool that made
 * the thing is the only place that knows both ids without a round trip, so it asks for the move
 * itself rather than hoping the model remembers to call `platform.navigate` afterwards.
 *
 * A REQUEST, not a jump: the shell owns routing and validates the path through its own router (see
 * app-shell's `vitesse-ai-route` listener), so this cannot send anyone to a page that does not
 * exist. Fire-and-forget — a write that succeeded is not undone by a shell that ignored it.
 */
function askToOpen(path: string): void {
  try {
    window.dispatchEvent(new CustomEvent('vitesse-ai-route', { detail: { path } }));
  } catch {
    // No window (tests, a worker): the write stands, only the move is lost.
  }
}

/** SCXML is unbounded and a prompt is not. Enough to reason about a statechart, and a clear line
 *  saying what was left off, rather than a silent truncation the model reads as the whole file. */
const MAX_SCXML_CHARS = 24_000;

/** A machine of an array child, `sensors/3`, and its array's name. A single child's path has no `/`. */
const ARRAY_MACHINE = /^(.+)\/(\d+)$/;
/** How many faulted machines of one array the read names with their faults; it counts the rest. */
const FAULTS_NAMED = 10;

/**
 * One array's machines in a few words (Step 4): how many run and in which states, how many are
 * stopped, and how many are faulted, with the first FAULTS_NAMED of them named. An array can have
 * 256 machines, and a list of each one would fill the reply.
 */
function arrayNote(name: string, machines: readonly ModelMachine[]): string {
  const running = machines.filter((m) => !m.stopped && !m.fault);
  const stopped = machines.filter((m) => m.stopped).length;
  const faulted = machines.filter((m) => !m.stopped && m.fault);
  const states = new Map<string, number>();
  for (const m of running) states.set(m.state || 'no state', (states.get(m.state || 'no state') ?? 0) + 1);
  const inStates = [...states].map(([state, n]) => `${state} ${n}`).join(', ');
  const named = faulted.slice(0, FAULTS_NAMED).map((m) => `${m.path} (${m.fault})`);
  if (faulted.length > FAULTS_NAMED) named.push(`and ${faulted.length - FAULTS_NAMED} more`);
  return (
    `${name}, an array of ${machines.length}: ${running.length} running${inStates ? ` (${inStates})` : ''}, ${stopped} stopped, ` +
    `${faulted.length} faulted${named.length > 0 ? `: ${named.join(', ')}` : ''}`
  );
}

/**
 * A group's children for the read's sentence: each by name with its state, and its fault when it
 * has one. A machine of an array that is stopped has no state and no fault. An array is one entry,
 * in arrayNote's words. The root is `currentState`, so it is left out. Empty for a single model.
 */
function machinesNote(machines: readonly ModelMachine[]): string {
  const { arrays } = machinesByArray(machines);
  const parts: string[] = [];
  for (const m of machines) {
    if (m.path === '') continue;
    const name = ARRAY_MACHINE.exec(m.path)?.[1];
    if (name === undefined)
      parts.push(m.stopped ? `${m.path} stopped` : `${m.path} in ${m.state || 'no state'}${m.fault ? ` (faulted: ${m.fault})` : ''}`);
    else if (arrays.get(name)?.[0] === m) parts.push(arrayNote(name, arrays.get(name)!));
  }
  // An array's entry has commas of its own, so the entries are joined with '; ' when there is an array.
  return parts.length > 0 ? `; its machines: ${parts.join(arrays.size > 0 ? '; ' : ', ')}` : '';
}

/** The root and each single child, and the machines of each array by the array's name, each list in the runtime's order. */
function machinesByArray(machines: readonly ModelMachine[]): { singles: ModelMachine[]; arrays: Map<string, ModelMachine[]> } {
  const singles: ModelMachine[] = [];
  const arrays = new Map<string, ModelMachine[]>();
  for (const m of machines) {
    const name = ARRAY_MACHINE.exec(m.path)?.[1];
    if (name === undefined) singles.push(m);
    else if (arrays.has(name)) arrays.get(name)!.push(m);
    else arrays.set(name, [m]);
  }
  return { singles, arrays };
}

/**
 * A group's machines as the tools' data gives them: the root and each single child as the runtime
 * reports them, and each array as one summary, for the reason that arrayNote gives. The summary
 * lists the first FAULTS_NAMED faulted machines and counts the rest (`moreFaulted`): when a PLC
 * disconnects, all 256 machines of a row can fault.
 */
function machinesData(machines: readonly ModelMachine[]): { machines: ModelMachine[]; arrays?: object[] } {
  const { singles, arrays } = machinesByArray(machines);
  if (arrays.size === 0) return { machines: [...machines] };
  return {
    machines: singles,
    arrays: [...arrays].map(([name, list]) => ({
      name,
      count: list.length,
      running: list.filter((m) => !m.stopped && !m.fault).length,
      stopped: list.filter((m) => m.stopped).length,
      ...faultedData(list.filter((m) => !m.stopped && m.fault)),
    })),
  };
}

/** An array's faulted machines as data: the first FAULTS_NAMED, and how many more there are. */
function faultedData(faulted: readonly ModelMachine[]): { faulted: object[]; moreFaulted?: number } {
  const listed = faulted.slice(0, FAULTS_NAMED).map((m) => ({ path: m.path, state: m.state, fault: m.fault }));
  return faulted.length > FAULTS_NAMED ? { faulted: listed, moreFaulted: faulted.length - FAULTS_NAMED } : { faulted: listed };
}

/** The five reserved lifecycle names every model accepts, whatever it declares of its own. */
const LIFECYCLE = new Set(['start', 'stop', 'pause', 'resume', 'reset']);

/** The first segments of the event names only the engine and the runner raise (services/model-runner/src/main/commandName.ts). */
const RESERVED_EVENT_HEADS = new Set(['done', 'error', 'vitesse']);

/** A name a model cannot declare as a command: the runner handles it itself, or refuses it, and the document never receives it. */
const isReservedCommand = (name: string): boolean => LIFECYCLE.has(name) || RESERVED_EVENT_HEADS.has(name.split('.', 1)[0] ?? '');

/*
 * THE GATE: A MODEL THAT CAN NEVER GET A VALUE IS REFUSED.
 *
 * A model reads each declared input by its name, and only a connection gives that name a value:
 * the root's own wiring, or a child's (Part 4 of docs/hierarchical-models-step-3-plan.md).
 * model-runner subscribes to each outside path that the wiring connects, and to nothing else. A
 * model with no connection boots, reports running, and reacts to nothing. The author's own
 * instructions describe that, and end with the sentence that matters: "nothing will tell you."
 *
 * So the write refuses it. This was once a warning on a write that went through, and an author
 * wrote past it: it reported a model as fixed while the model subscribed to nothing. A model
 * that is meant to have no live input is still legal (a pure timer, or a parent composed only of
 * other models). The author opens that door with `noLiveInputs`, which is a decision and not a
 * sentence to skim. A model that declares inputs which nothing feeds yet is written, and the
 * reply says that each input still needs a connection.
 */

/**
 * A path that models.bind connects: a controller symbol or another model's output.
 *
 * Stricter than the runner's own test in 2 ways. It wants segments after the prefix:
 * `controllers/` alone can only resolve to nothing. And it leaves out `integrations/`, which the
 * runner also reads. A bare name such as "Stacklight" is no path, and a connection to it connects
 * the input to nothing.
 */
export const isEntityPath = (v: string): boolean => /^(controllers|models|integrations)\/[^/]+\/[^/]+/.test(v.trim());

/** `record[key]` only when `key` is its own: a name such as "constructor" or "toString" is no entry. */
const ownEntry = <T>(record: Readonly<Record<string, T>>, key: string): T | undefined =>
  Object.hasOwn(record, key) ? record[key] : undefined;

/** The warnings on a write that went through. */
const WRITE_WARNINGS = 'Warnings — the write went through; fix one only if it is a mistake:';

/** The outline, then the saved document's problems and warnings, as models.read gives them. */
function describedNote(scxml: string, { outline, check }: NonNullable<Awaited<ReturnType<typeof describeDocument>>>): string {
  const head = outline.shortened
    ? `\nOutline, shortened: the full outline is ${outline.lines} lines, more than ${OUTLINE_MAX_LINES}, so this shows the datamodel and the top-level states, each with the number of states inside it:\n`
    : '\nOutline — every state and datamodel variable, with its line:\n';
  return (
    (outline.text ? head + outline.text : '') +
    (check.refusing.length > 0
      ? `\nThe saved document has ${problems(check.refusing.length)} that models.write would refuse; any rewrite must fix them:\n${listFindings(scxml, check.refusing)}`
      : '') +
    (check.warnings.length > 0 ? `\nWarnings on the saved document:\n${listFindings(scxml, check.warnings)}` : '')
  );
}

/** The gate's refusal (see THE GATE above). */
const NOTHING_CONNECTED =
  'Refused: as written this model can never receive a value. It declares no input, and no input of it or of a child is' +
  ' connected to a path. Declare each input it reads in inputs and connect it with path; a child is connected in children.' +
  ' If that is what you meant (a pure timer, or a parent composed only of other models), pass noLiveInputs: true and this' +
  ' will write it.';

const CONNECTED_TO_NOTHING_YET =
  ' It declares inputs that nothing is connected to yet, so it receives no values until each is connected: path in inputs' +
  " or models.bind for the model's own, and path in children for a child's.";

/** The mask replaces the whole map of the root's wiring, so a relay that sends none to keep would lose it. */
const OLD_RELAY_WIRING = (name: string): string =>
  `Nothing was written: this relay is older than Part 5 of the Step 3 plan, so it does not send the wiring of "${name}" to keep,` +
  ' and a write of its connections would erase the others. Tell the person it cannot be connected here until the relay is updated.';

type Scalar = string | number | boolean;
const isScalar = (v: unknown): v is Scalar => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

/**
 * The most machines one array child can have: statechart-host's `MAX_ARRAY_COUNT` (Decision 5 of
 * docs/hierarchical-models-step-4-plan.md). A copy, because the shell loads the host only on first
 * use. test/model-write-array.test.ts holds the two together.
 */
export const MAX_ARRAY_COUNT = 256;

const countRefusal = (child: string, count: unknown): string =>
  `The child "${child}" has count ${JSON.stringify(count)}. A count is the number of machines in an array: a whole number` +
  ` from 1 to ${MAX_ARRAY_COUNT}. For a single child, leave out count.`;

const pathRefusal = (what: string, path: string, after = ''): string =>
  `"${path}" is not an entity path, so the ${what} would be connected to nothing. Use the full path, as symbols.search` +
  " returns it: controllers/<controller-id>/symbols/<symbol-path>, models/<model-id>/variables/<name> for another model's output," +
  ' or integrations/<integration-id>/<target> for a target that integrations.browse names.' +
  after;

/** How a child's output drives nothing: the children are the complete list, so an output left out is not connected. */
const CHILD_DRIVES_NOTHING = ' To drive nothing, leave the output out of the child\'s outputs, or give it path "".';

/*
 * THE ROOT'S VERSION: A NEW ROOT DOCUMENT STARTS FROM THE SAVED ONE.
 *
 * A write replaces the root's whole document, and nothing made the author read that document first.
 * On 26 October 2026, 4 of 5 SCXML author runs (GPT-5.4 mini) wrote a new root for a saved cell
 * that they never read, to change 1 template and 1 connection. Had the wiring check let them through,
 * each write would have deleted the cell's process. So an edit that sends a root with other text
 * gives the version of the saved root, which models.read and every models.write reply give. An author
 * that did not read the document cannot know it, and one that read it before another write sees that
 * it changed. An edit that keeps the root leaves out scxml and needs no version. A create has
 * nothing to lose.
 */
async function rootVersionOf(scxml: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(scxml));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 12);
}

const ROOT_NOT_READ = (name: string): string =>
  `Not written: this would replace the root document of "${name}", and it gives no rootVersion. Read the model with` +
  ' models.read, with no doc, and send its document with your changes and the rootVersion that it gives. To change only' +
  ' templates, children or connections, leave out scxml: the saved root stays as it is.';

const ROOT_CHANGED = (name: string): string =>
  `Not written: that rootVersion is not the version of the saved root document of "${name}". The document changed after` +
  ' you read it, or the version is not one that models.read gave. Read it again with models.read, with no doc, and write from that.';

/**
 * THE ROOT'S OWN WIRING, from the write's input and output lists (Part 5 of the Step 3 plan). An
 * input with a path or a constant value is connected; an output with a path drives that PLC symbol,
 * with its safe value. A path of "" disconnects the port, and a port given neither keeps the
 * connection it has, so a write that only adds a port does not drop the others. A string, when it
 * is a problem to report.
 */
function rootWiringOf(
  rawInputs: unknown[] | undefined,
  rawOutputs: unknown[] | undefined,
): { inputs?: Record<string, DraftBinding | null>; outputs?: Record<string, DraftPlcOutput | null> } | string {
  let inputs: Record<string, DraftBinding | null> | undefined;
  for (const raw of rawInputs ?? []) {
    const i = raw as { name?: unknown; path?: unknown; value?: unknown };
    const port = String(i?.name ?? '').trim();
    if (i?.path === undefined && i?.value === undefined) continue;
    if (i.path !== undefined && i.value !== undefined) return `The input "${port}" has both a path and a value: give one.`;
    inputs ??= {};
    if (i.value !== undefined) {
      if (!isScalar(i.value)) return `The value of the input "${port}" must be a string, a number, or true or false.`;
      inputs[port] = { literal: literalFrom(i.value) };
    } else if (i.path === '') inputs[port] = null;
    else if (typeof i.path !== 'string' || !isEntityPath(i.path)) return pathRefusal(`input "${port}"`, String(i.path));
    else inputs[port] = { path: i.path.trim() };
  }
  let outputs: Record<string, DraftPlcOutput | null> | undefined;
  for (const raw of rawOutputs ?? []) {
    const o = raw as { name?: unknown; path?: unknown; safe?: unknown };
    const port = String(o?.name ?? '').trim();
    if (o?.path === undefined && o?.safe === undefined) continue;
    // A safe value with no path is not a disconnect: a path not given keeps the connection, which safe would change.
    if (o.path === undefined)
      return (
        `The output "${port}" has safe but no path. safe is the value that the symbol in path gets when the model faults:` +
        ' give path with it, leave out both to keep the connection, or give path "" to disconnect it.'
      );
    outputs ??= {};
    if (o.path === '') {
      outputs[port] = null;
      continue;
    }
    if (typeof o.path !== 'string' || !isEntityPath(o.path)) return pathRefusal(`output "${port}"`, String(o.path));
    if (!isScalar(o.safe))
      return `The output "${port}" drives ${o.path}, so it needs safe: the value that symbol gets when the model faults.`;
    outputs[port] = { path: o.path.trim(), safe: literalFrom(o.safe) };
  }
  return { ...(inputs ? { inputs } : {}), ...(outputs ? { outputs } : {}) };
}

/** A port list as a write gives it, each port with a name and a type. A string, when it is a problem to report. */
function portListOf(raw: unknown, what: string): DraftTemplate['iface']['inputs'] | string {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) return `The ${what} must be a list of ports.`;
  const ports = raw.map((p) => {
    const x = p as { name?: unknown; type?: unknown; unit?: unknown };
    const unit = String(x?.unit ?? '').trim();
    return { name: String(x?.name ?? '').trim(), type: String(x?.type ?? '').trim(), ...(unit ? { unit } : {}) };
  });
  return ports.some((p) => !p.name || !p.type) ? `Every port of the ${what} needs a name and a type.` : ports;
}

/**
 * A child's ports as a write gives them: an object by port name, as the schema says, or a list of
 * entries that each have a name. GPT-5.4 mini sent a list on 26 October 2026, and each entry was
 * read as a port named by its index ("part_sensor_1.0"). A string, when it is a problem to report.
 */
function portMapOf<T>(raw: unknown, what: string): Record<string, T> | string {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== 'object') return `The ${what} must be an object by port name.`;
  if (!Array.isArray(raw)) return raw as Record<string, T>;
  const ports: Record<string, T> = {};
  for (const entry of raw) {
    const port = String((entry as { name?: unknown })?.name ?? '').trim();
    if (!port) return `Every port in the ${what} needs a name.`;
    if (Object.hasOwn(ports, port)) return `The port "${port}" is given twice in the ${what}.`;
    ports[port] = entry as T;
  }
  return ports;
}

/**
 * A GROUP, as a write gives it (Part 5 of the Step 3 plan): templates by name, each a document with
 * its ports, and the complete list of children, each a template with its connections. A template
 * with an empty document is removed. A child's input is a path, another child's name (the
 * building's floors read the elevator as `lift`), or a constant; its PLC output is a path with a
 * safe value. A path of "" is no path, as it is for the root's ports: a child's input with neither
 * is not connected (the root can still assign it), and a child's output with none drives nothing. A
 * model that fills every field it is offered sends "" for the paths it did not mean. A string, when
 * it is a problem to report.
 *
 * AN ARRAY (Step 4) is a child with a count: that many machines of its template, `sensors/0` to
 * `sensors/7`. A path in its wiring can hold `{i}`, the index, or `{n}`, the index plus 1, so that
 * each machine reads and writes its own symbol (`In{n}`). Its inputs can also feed the platform's
 * `enabled`. An input can read another machine's output: `belt/running`, or `belts/{i}/running` for
 * the machine of the same index in the array `belts`. The wiring check (checkDraft) then refuses
 * what only the whole group shows: a PLC output with no index, a port that is not there, or a
 * template that declares `enabled` or `faulted`.
 */
function groupOf(
  rawTemplates: unknown,
  rawChildren: unknown,
): { templates?: Record<string, DraftTemplate | null>; children?: DraftChild[] } | string {
  let templates: Record<string, DraftTemplate | null> | undefined;
  if (rawTemplates !== undefined) {
    if (!Array.isArray(rawTemplates)) return 'templates must be a list.';
    templates = {};
    for (const raw of rawTemplates) {
      const t = raw as { name?: unknown; scxml?: unknown; inputs?: unknown; outputs?: unknown };
      const name = String(t?.name ?? '').trim();
      if (!name) return 'Every template needs a name.';
      if (Object.hasOwn(templates, name)) return `The template "${name}" is given twice.`;
      const scxml = typeof t.scxml === 'string' ? t.scxml : '';
      if (!scxml.trim()) {
        templates[name] = null;
        continue;
      }
      const inputs = portListOf(t.inputs, `template "${name}"'s inputs`);
      if (typeof inputs === 'string') return inputs;
      const outputs = portListOf(t.outputs, `template "${name}"'s outputs`);
      if (typeof outputs === 'string') return outputs;
      templates[name] = { scxml, iface: { inputs, outputs, commands: [] } };
    }
  }
  let children: DraftChild[] | undefined;
  if (rawChildren !== undefined) {
    if (!Array.isArray(rawChildren)) return 'children must be a list.';
    const named = new Set(rawChildren.map((c) => String((c as { name?: unknown })?.name ?? '').trim()));
    // Another child of the write, by its name (`lift`), or one of its outputs, as the host reads a port path:
    // `belt/running`, and for a machine of an array `belts/3/running` or `belts/{i}/running`. The wiring check
    // (checkDraft) judges the port and the index.
    const namesMachine = (path: string, self: string): boolean => {
      const slash = path.indexOf('/');
      return slash < 0 ? named.has(path) && path !== self : named.has(path.slice(0, slash)) && slash < path.length - 1;
    };
    children = [];
    for (const raw of rawChildren) {
      const c = raw as { name?: unknown; template?: unknown; count?: unknown; inputs?: unknown; outputs?: unknown };
      const name = String(c?.name ?? '').trim();
      const template = String(c?.template ?? '').trim();
      if (!name || !template) return 'Every child needs a name and a template.';
      if (children.some((x) => x.name === name)) return `The child "${name}" is given twice.`;
      const count = c.count;
      if (count !== undefined && (typeof count !== 'number' || !Number.isInteger(count) || count < 1 || count > MAX_ARRAY_COUNT))
        return countRefusal(name, count);
      const rawInputs = portMapOf<{ path?: unknown; value?: unknown }>(c.inputs, `inputs of the child "${name}"`);
      if (typeof rawInputs === 'string') return rawInputs;
      const inputs: Record<string, DraftBinding> = {};
      const needsOne = (port: string): string =>
        `The input ${name}.${port} needs one of: a path (controllers/…, models/…, another child's name, or <child>/<output>), or a value.`;
      for (const [port, b] of Object.entries(rawInputs)) {
        if (b === null || typeof b !== 'object') return needsOne(port);
        const path = b.path === '' ? undefined : b.path;
        if (path === undefined && b.value === undefined) continue;
        if (path === undefined && isScalar(b.value)) inputs[port] = { literal: literalFrom(b.value) };
        else if (typeof path === 'string' && b.value === undefined && (isEntityPath(path) || namesMachine(path, name)))
          inputs[port] = { path: path.trim() };
        else return needsOne(port);
      }
      const rawOutputs = portMapOf<{ path?: unknown; safe?: unknown }>(c.outputs, `outputs of the child "${name}"`);
      if (typeof rawOutputs === 'string') return rawOutputs;
      const outputs: Record<string, DraftPlcOutput> = {};
      for (const [port, o] of Object.entries(rawOutputs)) {
        if (o === null || typeof o !== 'object')
          return `The output ${name}.${port} must be {"path": "controllers/…", "safe": its value when the machine faults}.${CHILD_DRIVES_NOTHING}`;
        if (o.path === undefined || o.path === '') continue;
        if (typeof o.path !== 'string' || !isEntityPath(o.path))
          return pathRefusal(`output ${name}.${port}`, String(o.path), CHILD_DRIVES_NOTHING);
        if (!isScalar(o.safe))
          return `The output ${name}.${port} drives ${o.path}, so it needs safe: the value that symbol gets when the machine faults.`;
        outputs[port] = { path: o.path.trim(), safe: literalFrom(o.safe) };
      }
      children.push({ name, template, ...(count !== undefined ? { count } : {}), inputs, outputs });
    }
  }
  return { ...(templates ? { templates } : {}), ...(children ? { children } : {}) };
}

/** A template's port in the write's schema. */
const PORT = {
  type: 'object',
  properties: { name: { type: 'string' }, type: { type: 'string' }, unit: { type: 'string' } },
  required: ['name', 'type'],
  additionalProperties: false,
};

/** The ports a write declares that its document has no <data id="…"> for: an output is a variable the document assigns. */
const missingData = (scxml: string, outputs: readonly { name: string }[]): string[] =>
  outputs
    .filter((o) => !new RegExp(`<data\\s[^>]*\\bid="${o.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`).test(scxml))
    .map((o) => o.name);

const tooLongToEdit = (name: string, length: number): string =>
  `Refused: "${name}" is ${length} characters of SCXML, and models.read shows only the first ${MAX_SCXML_CHARS}. A write` +
  ' replaces the whole document, so a rewrite from what you have read would delete the rest of the model. Do not edit it:' +
  ' tell the person that this model is too large to change through the assistant.';

/** A rename reply lists this many changed lines; a rename across a long model can touch hundreds. */
const RENAME_LINES_LISTED = 20;

const notStoppedForRename = (name: string, status: string): string =>
  `Not renamed: "${name}" is not stopped (it is ${status === 'running' || status === 'paused' ? status : `set to run, with status ${status}`}),` +
  ' and only a stopped model can be renamed. A running or paused' +
  ' model keeps a checkpoint of its state, saved under the old names. When it resumes from that checkpoint, a renamed' +
  ' state is not there, so the model starts again from its initial state, and a renamed variable comes back empty. A stop' +
  ' clears the checkpoint. Ask the person whether to stop the model — it then stops acting on the machine — and rename it after that.';

const renamedLines = (lines: number[]): string =>
  lines.length === 1
    ? `line ${lines[0]}`
    : `lines ${lines.slice(0, RENAME_LINES_LISTED).join(', ')}${lines.length > RENAME_LINES_LISTED ? ` and ${lines.length - RENAME_LINES_LISTED} more` : ''}`;

/** Each finding as one key, counted, so the same finding before and after a rename cancels out. */
function newFindings(before: LanguageDiagnostic[], after: LanguageDiagnostic[]): LanguageDiagnostic[] {
  const key = (d: LanguageDiagnostic): string => `${d.code}\u0000${d.message}`;
  const had = new Map<string, number>();
  for (const d of before) had.set(key(d), (had.get(key(d)) ?? 0) + 1);
  return after.filter((d) => {
    const n = had.get(key(d)) ?? 0;
    if (n > 0) had.set(key(d), n - 1);
    return n === 0;
  });
}

export function registerModelTools(deps: ModelToolDeps): () => void {
  const list: ClientTool = {
    name: 'models.list',
    // Models/Subscribe.
    permission: Permission.MODEL_VIEW,
    description:
      "List the state machines (SCXML models) on a relay: each one's run state, the inputs and outputs it declares, " +
      'and what each input is connected to. ' +
      'Use this to find a model by name, to see which model already watches a symbol, or to answer what is running here. ' +
      'It carries everything about a model except its SCXML, so read a model only when you need its source.',
    parameters: { type: 'object', properties: { gateway: GATEWAY_PARAM }, additionalProperties: false },
    run: (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const models = entry.models.all;
      if (models.length === 0) {
        // An empty relay and a failed read look identical unless one of them says so.
        return { ok: true, message: `The relay "${gw.value.name}" has no models on it.`, data: { models: [] } };
      }
      /*
       * THE WIRING TRAVELS WITH THE LIST, because it is already in hand, and asking for it cost a
       * round trip each.
       *
       * A list of names and run states cannot answer the questions this surface is asked: "which
       * model already watches that symbol", "what does this one still need connected", "does
       * anything read the stacklight". To answer one of them, the author called models.read once
       * for each candidate, and models.read fetches the whole SCXML. The interface and the root's
       * wiring arrive on the descriptor stream that fills this view, so none of this is a second
       * request. A group's children are listed with their wiring too.
       *
       * The source is still a read. It is the one field that is unbounded, the one that the lean
       * descriptor stream strips, and the only one that a lister does not need.
       */
      const rows = models.map((m) => ({
        id: m.id,
        name: m.name,
        status: m.status,
        desiredState: m.desiredState,
        // The ACTIVE state ids, which is what "what is it doing" usually means — distinct from
        // `status`, which is whether the interpreter is running at all.
        currentState: m.currentState,
        // A group's machines, each with its own state and fault; `currentState` is the root's.
        // An array is one summary: a list of each of its machines can be 256 entries long.
        ...(m.machines?.length ? machinesData(m.machines) : {}),
        error: m.error,
        inputs: m.inputs ?? [],
        outputs: m.outputs ?? [],
        // The root's own wiring: each input's path or constant, and each output's PLC symbol with its safe value.
        wiring: m.wiring ?? { inputs: [], outputs: [] },
        ...(m.children?.length ? { children: m.children } : {}),
      }));
      // An input a model declares and nothing connects is the defect this surface exists to catch.
      // The sentence names it, so it is seen without anyone reading `data`.
      const isConnected = (r: (typeof rows)[number], input: string): boolean =>
        r.wiring.inputs.some((p) => p.name === input && (p.path !== '' || p.constant !== undefined));
      const unconnected = rows
        .map((r) => ({ name: r.name, missing: r.inputs.filter((i) => !isConnected(r, i)) }))
        .filter((x) => x.missing.length);
      return {
        ok: true,
        message:
          `${rows.length} model${rows.length === 1 ? '' : 's'} on "${gw.value.name}": ${rows.map((r) => `${r.name} (${r.status})`).join(', ')}.` +
          (unconnected.length
            ? ` Connected to nothing yet: ${unconnected.map((u) => `${u.name} (${u.missing.join(', ')})`).join('; ')}.`
            : ''),
        data: { models: rows },
      };
    },
  };

  const read: ClientTool = {
    name: 'models.read',
    // Models/GetModelSource.
    permission: Permission.MODEL_SOURCE_VIEW,
    description:
      'Read one state machine: its SCXML source, an outline of every state and datamodel variable, any problems in the ' +
      'saved document, its declared inputs and outputs, what each input is connected to, and its current state. Use it to ' +
      'explain what a model does, why it is in the state it is in, or before suggesting a change to it. For a group, it also lists ' +
      "the templates, the children and their wiring; pass doc with a template name to read that template's document. A source " +
      `longer than ${MAX_SCXML_CHARS} characters is cut, and such a model cannot be edited through models.write.`,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The model id, as returned by models.list.' },
        doc: {
          type: 'string',
          description: "A template's name, to read that template's document instead of the root's. Leave it out to read the root.",
        },
      },
      required: ['modelId'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '');
      const doc = typeof args.doc === 'string' ? args.doc : '';
      const view = entry.models.get(modelId);
      if (!view) {
        // Name what IS there: a model id the assistant guessed is the common failure, and a list is
        // the fix it can act on without another round trip.
        const known = entry.models.all.map((m) => m.id);
        return {
          ok: false,
          message: `No model "${modelId}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ''}`,
        };
      }
      let scxml = '';
      let source: Awaited<ReturnType<typeof entry.models.getSource>> | undefined;
      try {
        source = await entry.models.getSource(modelId);
        scxml = source.scxml;
      } catch (e) {
        // The source did not load, so nothing of the model is described: the read can be tried again.
        return {
          ok: false,
          message: `Could not read the source of "${view.name}": ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      // The whole model: a group's templates, children and wiring (Part 5).
      const draft = source ? draftOf(source, view) : null;
      if (doc) {
        const template = draft ? ownEntry(draft.templates, doc) : undefined;
        if (!template)
          return {
            ok: false,
            message:
              `"${view.name}" has no template "${doc}". To read the root document, leave out doc.` +
              (draft && Object.keys(draft.templates).length > 0
                ? ` Its templates: ${Object.keys(draft.templates).join(', ')}.`
                : ' It has no templates.'),
          };
        const options = ownEntry(optionsOf(draft!).templates, doc) ?? {};
        const described = await describeDocument(template.scxml, options);
        const cut = template.scxml.length > MAX_SCXML_CHARS;
        return {
          ok: true,
          message:
            `Template "${doc}" of "${view.name}": inputs ${template.iface.inputs.map((p) => `${p.name} ${p.type}`).join(', ') || 'none'}; ` +
            `outputs ${template.iface.outputs.map((p) => `${p.name} ${p.type}`).join(', ') || 'none'}.` +
            (cut
              ? ` Its document is ${template.scxml.length} characters, cut to the first ${MAX_SCXML_CHARS}; the outline covers all of it.`
              : '') +
            (described ? describedNote(template.scxml, described) : ''),
          data: { id: view.id, template: doc, scxml: cut ? template.scxml.slice(0, MAX_SCXML_CHARS) : template.scxml, scxmlTruncated: cut },
        };
      }
      const truncated = scxml.length > MAX_SCXML_CHARS;
      /*
       * THE OUTLINE AND THE FINDINGS, from the whole source, not the part handed back. For a model
       * cut at MAX_SCXML_CHARS the outline is the only view of all of it; for any model it says which
       * variable is an input, an output or the model's own. The findings are the saved document's:
       * one a person wrote, or one saved before the check existed, can carry faults the author must
       * fix in any rewrite. See model-check.ts for the outline's limit.
       */
      const described = await describeDocument(
        scxml,
        // The draft's ports come from the descriptor; a relay older than Part 5 sends none, and the view has them.
        draft && source?.descriptor
          ? optionsOf(draft).root
          : {
              inputs: view.inputs ?? [],
              outputs: view.outputs ?? [],
              commands: view.commands ?? [],
              types: portTypes(view.iface),
            },
      );
      // What a write that replaces this document gives (see THE ROOT'S VERSION).
      const rootVersion = await rootVersionOf(scxml);
      return {
        ok: true,
        message:
          `"${view.name}" is ${view.status}${view.currentState ? `, currently in ${view.currentState}` : ''}` +
          `${view.error ? `, with the error: ${view.error}` : ''}${machinesNote(view.machines ?? [])}.` +
          (truncated
            ? ` Its SCXML is ${scxml.length} characters and has been cut to the first ${MAX_SCXML_CHARS}, so do not rewrite it:` +
              ' a write replaces the whole document, and models.write refuses an edit of a model this long. The outline below covers all of it.'
            : ` Its rootVersion is ${rootVersion}: give it to models.write with a changed root document.`) +
          (draft ? groupNote(draft) : '') +
          (draft?.notify
            ? ` Each state change, and the model's start and stop, send ${draft.notify.slackPerson} a Slack message` +
              (draft.notify.slackTimeVariable ? `; the start and stop time is "${draft.notify.slackTimeVariable}".` : '.')
            : '') +
          (described ? describedNote(scxml, described) : ''),
        data: {
          id: view.id,
          name: view.name,
          ...(draft?.notify
            ? {
                slackNotify: {
                  person: draft.notify.slackPerson,
                  tool: draft.notify.slackTool,
                  ...(draft.notify.slackTimeVariable ? { timeVariable: draft.notify.slackTimeVariable } : {}),
                },
              }
            : {}),
          status: view.status,
          currentState: view.currentState,
          ...machinesData(view.machines ?? []),
          desiredState: view.desiredState,
          error: view.error,
          inputs: view.inputs ?? [],
          outputs: view.outputs ?? [],
          wiring: view.wiring ?? { inputs: [], outputs: [] },
          scxml: truncated ? scxml.slice(0, MAX_SCXML_CHARS) : scxml,
          scxmlTruncated: truncated,
          ...(truncated ? {} : { rootVersion }),
          outlineShortened: described?.outline.shortened ?? false,
        },
      };
    },
  };

  /*
   * THE WRITE VERB — one, for create and edit alike, because the platform has one.
   *
   * `Models.Update` is the single authoring RPC, and `updateMask` tells the two cases apart.
   * Choosing it correctly is the whole safety story of this tool:
   *
   *   · AN EDIT names only the parts it changed: the document always, and the interface, the
   *     root's wiring, the templates or the children when the write changes them. The
   *     model-runner merges only the named fields into the descriptor it owns, so the connections
   *     a person made stay. A mask of ["*"] here would erase them, and the model would come back
   *     connected to nothing.
   *   · A CREATE sends every part: there is no descriptor to merge into.
   *
   * IT CHECKS, THEN THE GATEWAY DECIDES. The model is checked here first (model-draft.ts), so a
   * refusal names every fault with its line. The model-runner still checks every write and
   * refuses one it cannot run, with its own words. That is the gate for every writer, not only
   * this one, and its refusal is returned as it is.
   *
   * A WHOLE MODEL IN ONE WRITE (Part 5 of docs/hierarchical-models-step-3-plan.md). Every write
   * reads the saved model first (GetModelSource, with the rest of the descriptor), puts the
   * write's parts on it, checks every document and the wiring together, and saves the parts that
   * changed in one write. A model is a `datamodel="typescript"` document that reads each declared
   * input by its name. An input given a path is connected through the root's own wiring (Part 4),
   * which is the one way an input gets a value, and an output given a path drives that PLC symbol
   * with its safe value. A group's templates and children go in the same write: a template sent
   * replaces the one of its name, and the children are sent as the complete list.
   */
  const write: ClientTool = {
    name: 'models.write',
    // Models/GetModelSource, then Models/Create or Models/Update.
    permission: Permission.MODEL_EDIT,
    description:
      'Create a state machine, or replace the SCXML of one that exists. ' +
      "Pass an existing modelId to edit it, or a new one to create it. Editing keeps the model's connections and declared interface unless you pass `inputs` or `outputs`. " +
      'An edit that changes the root document gives the rootVersion that models.read gave; an edit that keeps it leaves out scxml. ' +
      'Publishing is DECLARED: a datamodel variable reaches a board or another model only if it is listed in `outputs` — rewriting the SCXML alone publishes nothing. ' +
      'An input given a path is connected to that symbol; an output given a path drives that PLC symbol or integration target, with its safe value. ' +
      'A group is written in the same call: `templates`, each a document with its ports, and `children`, each a template run as a part, with its connections. ' +
      'A model you create starts STOPPED — the user starts it when they are satisfied with it. ' +
      'When the person asks for a Slack message at each state change, pass slackNotifyTo in the same call: the model also sends ' +
      '"<model> started at <time>" and "<model> stopped at <time>" when it starts and stops, and slackTimeVariable names the output or input that holds that time. ' +
      'The model is checked before it is sent (every document of a group, the types and the wiring): if it has errors, or uses a name nothing declares, nothing is written and ' +
      'the reply lists every problem with its line — fix them all and write again.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The model to write. An id that does not exist creates that model.' },
        scxml: {
          type: 'string',
          description:
            'The complete SCXML document of the root. It replaces the saved one entirely. Required to create. On an edit, leave it ' +
            'out to keep the saved root, for example to change only templates, children or connections.',
        },
        rootVersion: {
          type: 'string',
          description:
            'On an edit that sends a changed scxml: the rootVersion of the saved root, as models.read (with no doc) or your last ' +
            'models.write of this model gave it.',
        },
        name: { type: 'string', description: 'Display name. Required when creating; omit when editing to leave it alone.' },
        inputs: {
          type: 'array',
          description:
            'The inputs this model DECLARES. The SCXML reads each by its name, and only a connection gives it a value: ' +
            'path here, or models.bind later. An input that is not declared and connected never gets a value. ' +
            'Omit to leave the declared interface alone.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The input name, matching what the SCXML reads.' },
              type: { type: 'string', description: 'Its type, e.g. STRING, BOOL, REAL, INT.' },
              path: {
                type: 'string',
                description:
                  'Connects it: controllers/<id>/symbols/<path>, models/<id>/variables/<name>, or integrations/<id>/<target>. "" disconnects it; omit to keep its connection.',
              },
              value: { type: ['string', 'number', 'boolean'], description: 'Or connects it to a constant.' },
            },
            required: ['name', 'type'],
            additionalProperties: false,
          },
        },
        outputs: {
          type: 'array',
          description:
            'The outputs this model DECLARES it publishes — the datamodel variables a board, another model or a person can ' +
            'read as models/<modelId>/variables/<name>. ONLY variables named here are published; the rest of the datamodel ' +
            'stays private. A model whose numbers should show on a dashboard must declare them here, or every tile bound ' +
            'to it stays empty. Omit to leave the declared outputs alone.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The datamodel variable, exactly as the SCXML names it.' },
              type: { type: 'string', description: 'Its type: BOOL, INT, DINT, REAL or STRING.' },
              unit: { type: 'string', description: 'Optional display unit, e.g. °C, %, pcs.' },
              path: {
                type: 'string',
                description:
                  'What it drives: a PLC symbol, controllers/<id>/symbols/<path>, or an integration target, integrations/<id>/<target>. ' +
                  'A table target gets 1 row for each change: an object value gives 1 column for each key, a scalar goes into a column named value. ' +
                  '"" disconnects it.',
              },
              safe: { type: ['string', 'number', 'boolean'], description: 'With path: the value that symbol gets when the model faults.' },
            },
            required: ['name', 'type'],
            additionalProperties: false,
          },
        },
        commands: {
          type: 'array',
          description:
            'The commands this model DECLARES an operator can send it by hand — each an SCXML event the document handles ' +
            'with <transition event="…">. A declared command shows up as a button on the model page and on model-commands ' +
            'tiles; an undeclared event is reachable by nobody. Clear a fault, a setpoint, a mode: whatever a person at the ' +
            'machine would want to do. Never name one start, stop, pause, resume or reset, or begin it with done., error. or ' +
            'vitesse.: the platform handles those names itself, so the document never receives them. Omit to leave the declared commands alone.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string', description: 'The event name, exactly as the <transition event="…"> spells it.' },
              params: {
                type: 'array',
                items: { type: 'string' },
                description: 'Optional event-data keys the transition reads, e.g. ["setpoint"].',
              },
            },
            required: ['name'],
            additionalProperties: false,
          },
        },
        noLiveInputs: {
          type: 'boolean',
          description:
            'Set true ONLY for a model that is MEANT to have no live inputs — a pure timer, or a parent composed entirely of ' +
            'other models. A model that declares no input and connects no input to a path is refused without it, because ' +
            'that is the shape that boots, reports running, and reacts to nothing.',
        },
        templates: {
          type: 'array',
          description:
            "A group's templates, each a complete document with its ports, written as the model's own. A template you send " +
            'replaces the one with its name, and the others stay; an empty scxml removes it. Omit to leave them alone.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              scxml: { type: 'string' },
              inputs: { type: 'array', items: PORT },
              outputs: { type: 'array', items: PORT },
            },
            required: ['name', 'scxml'],
            additionalProperties: false,
          },
        },
        children: {
          type: 'array',
          description:
            "A group's parts, each a template run as a machine that the root reads by name (ps1.present). The complete list: " +
            'a child left out is removed. Omit to leave them alone.',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              template: { type: 'string' },
              count: {
                type: 'integer',
                minimum: 1,
                maximum: MAX_ARRAY_COUNT,
                description:
                  'Makes the child an array of this many machines (sensors/0 to sensors/7), which the root reads as sensors[i]. ' +
                  'In its paths, {i} is the index and {n} the index plus 1 (In{n}). Omit for a single child.',
              },
              inputs: {
                type: 'object',
                description:
                  'Port to {"path": "controllers/…" or "<child>/<output>"} or {"value": a constant}. Or a name to {"path": "<another child>"}: ' +
                  'the document reads that child and sends it events by this name, which is not a port of the template. ' +
                  'An array can also feed enabled here. A port left out is not connected: the root can still assign it.',
                additionalProperties: {
                  type: 'object',
                  properties: { path: { type: 'string' }, value: { type: ['string', 'number', 'boolean'] } },
                  additionalProperties: false,
                },
              },
              outputs: {
                type: 'object',
                description:
                  'Port to {"path": "controllers/…" or "integrations/…", "safe": its value when the machine faults}: the PLC symbol or integration target it drives. ' +
                  'In an array, the path holds {i} or {n}, so each machine drives its own. A port left out drives nothing.',
                additionalProperties: {
                  type: 'object',
                  properties: { path: { type: 'string' }, safe: { type: ['string', 'number', 'boolean'] } },
                  required: ['path', 'safe'],
                  additionalProperties: false,
                },
              },
            },
            required: ['name', 'template'],
            additionalProperties: false,
          },
        },
        slackNotifyTo: {
          type: 'string',
          description:
            'Sends this person a Slack message at each state change of the model: "<model>: transitioned from <state> to <state>". ' +
            'Give their name, @handle or Slack member id as the person said it, for example for "send a Slack message to Colin on every state change". ' +
            'Omit it, or give "", to keep what the model has.',
        },
        slackNotifyOff: { type: 'boolean', description: 'Set true to stop the Slack messages of state changes.' },
        slackTimeVariable: {
          type: 'string',
          description:
            'The time in the start and stop lines: the name of a declared output or a declared input whose value is a time (an ISO string, epoch ms, a PLC clock), used as it is. ' +
            'Give it when the person says which time to use. Omit it, or give "", to keep the saved one; with none, the relay clock is used.',
        },
        slackTool: {
          type: 'string',
          description:
            'Only when a refusal says that more than one Slack server can send a direct message: the full name of the one to use, mcp__<server>__send_dm.',
        },
      },
      required: ['modelId'],
      additionalProperties: false,
    },
    run: async (args, ctx) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '').trim();
      const name = typeof args.name === 'string' ? args.name.trim() : '';
      if (!modelId) return { ok: false, message: 'A modelId is required — it is the id the model will have on this relay.' };

      /*
       * A MODEL TOO LONG TO HAVE BEEN READ WHOLE IS NOT REWRITTEN.
       *
       * models.read gives the first MAX_SCXML_CHARS of the source, and a write replaces the whole
       * document. An author that edits a longer model sends back the part it read, and the rest of
       * the model is deleted without a word. The check below catches only some of that (a kept
       * transition into a state that was cut off is an unknown target); a state nothing in the kept
       * part names would simply vanish. So the edit is refused before anything else is asked of it.
       * A create is not affected: there is nothing to lose.
       *
       * The cost is real: no screen lets a person edit SCXML, so a model this long cannot be changed
       * through the product until a tool can change part of a document. That is still better than an
       * edit that deletes most of the model.
       */
      const stored = entry.models.get(modelId);
      let savedSource: Awaited<ReturnType<typeof entry.models.getSource>> | undefined;
      if (stored) {
        try {
          savedSource = await entry.models.getSource(modelId);
        } catch (e) {
          return {
            ok: false,
            message: `Could not read the saved source of "${stored.name}" to check its length before the edit: ${e instanceof Error ? e.message : String(e)}`,
            retryable: true,
          };
        }
        const saved = savedSource.scxml;
        if (saved.length > MAX_SCXML_CHARS) return { ok: false, message: tooLongToEdit(stored.name, saved.length), retryable: false };
      }

      // THE ROOT OF AN EDIT (see THE ROOT'S VERSION): no scxml keeps the saved document, and other text needs its version.
      let scxml = String(args.scxml ?? '');
      if (!stored || !savedSource) {
        if (!scxml.trim()) return { ok: false, message: 'The scxml argument was empty. Send the complete document, not a fragment.' };
      } else if (!scxml.trim()) scxml = savedSource.scxml;
      else if (scxml !== savedSource.scxml) {
        const given = typeof args.rootVersion === 'string' ? args.rootVersion.trim() : '';
        if (!given) return { ok: false, message: ROOT_NOT_READ(stored.name), retryable: false };
        if (given !== (await rootVersionOf(savedSource.scxml))) return { ok: false, message: ROOT_CHANGED(stored.name), retryable: false };
      }

      /*
       * Parsed BEFORE either branch, and validated by name. A malformed input silently dropped here
       * would produce a model that declares nothing while reporting a successful write — the same
       * shape of quiet failure this whole parameter exists to remove.
       */
      const rawInputs = Array.isArray(args.inputs) ? (args.inputs as unknown[]) : undefined;
      const declared = rawInputs?.map((raw) => {
        const i = raw as { name?: unknown; type?: unknown };
        return {
          name: String(i?.name ?? '').trim(),
          type: String(i?.type ?? '').trim(),
          /*
           * kind 0 is ANY — bindable to a PLC symbol or to another model's variable. Not exposed as
           * a parameter and not defaulted to SYMBOL: narrowing it here would quietly rule out
           * model-to-model composition for every input an agent ever declares, which is a design
           * decision that does not belong in a default.
           */
          kind: 0,
          // An input the SCXML reads is one the model needs. A declaration the author did not ask to
          // be optional is a requirement, and saying so is what makes an unbound one visible.
          optional: false,
          isArray: false,
        };
      });
      if (declared?.some((i) => !i.name || !i.type))
        return { ok: false, message: 'Every declared input needs a name and a type — one of them was missing both or one.' };
      /*
       * OUTPUTS, THE OTHER HALF OF THE INTERFACE — and until this parameter existed, a half nothing
       * could set. Publishing is DECLARED (see ModelInterface.outputs): a datamodel variable is on
       * the wire only if its name is listed here. With inputs the only declarable thing, every
       * "publish these numbers" edit rewrote the SCXML, was told the interface was unchanged, read
       * the model back to an empty output list, and tried again. The variable must also exist in
       * the document, or the declaration publishes nothing and says nothing.
       */
      const rawOutputs = Array.isArray(args.outputs) ? (args.outputs as unknown[]) : undefined;
      const declaredOutputs = rawOutputs?.map((raw) => {
        const o = raw as { name?: unknown; type?: unknown; unit?: unknown };
        return { name: String(o?.name ?? '').trim(), type: String(o?.type ?? '').trim(), unit: String(o?.unit ?? '').trim() };
      });
      if (declaredOutputs?.some((o) => !o.name || !o.type))
        return { ok: false, message: 'Every declared output needs a name and a type — one of them was missing both or one.' };
      const undeclaredInDoc =
        declaredOutputs
          ?.filter((o) => !new RegExp(`<data\\s[^>]*\\bid="${o.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`).test(scxml))
          .map((o) => o.name) ?? [];
      if (undeclaredInDoc.length)
        return {
          ok: false,
          message: `Declared as outputs but not in the document's <datamodel>: ${undeclaredInDoc.join(', ')}. An output is a <data id="…"> the SCXML assigns; declare only what the document has.`,
        };

      /*
       * COMMANDS, THE THIRD DECLARABLE THING. A model nobody can drive by hand is a model a person
       * cannot try, pause or reset from a board, and until this parameter existed every write sent
       * `commands: []` — the author could put a <transition event="reset"> in the document and no
       * button would ever appear for it. Declared here, checked against the document the same way
       * outputs are: a command whose event no transition handles is a button that does nothing.
       */
      const rawCommands = Array.isArray(args.commands) ? (args.commands as unknown[]) : undefined;
      const declaredCommands = rawCommands?.map((raw) => {
        const c = raw as { name?: unknown; params?: unknown };
        return {
          name: String(c?.name ?? '').trim(),
          params: Array.isArray(c?.params) ? (c.params as unknown[]).map((x) => String(x).trim()).filter(Boolean) : [],
        };
      });
      if (declaredCommands?.some((c) => !c.name))
        return { ok: false, message: 'Every declared command needs a name — one was missing it.' };
      // A start button that only marks the model as running, a stop button that stops it: the relay refuses these too.
      const reservedCommands = [...new Set(declaredCommands?.filter((c) => isReservedCommand(c.name)).map((c) => c.name))];
      if (reservedCommands.length)
        return {
          ok: false,
          message:
            `Not sent: ${reservedCommands.join(', ')} cannot be declared as commands. The platform handles start, stop, pause, resume ` +
            'and reset itself, and refuses names that begin with done., error. or vitesse., so the document never receives them: ' +
            'a start button only marks the model as running, and a stop button stops it. Give each command a name that says what ' +
            'it does in the machine (for example runCycle or clearFault), and use that name in its <transition event="…">.',
        };
      const unhandledInDoc = declaredCommands?.filter((c) => !handlesCommand(scxml, c.name)).map((c) => c.name) ?? [];
      if (unhandledInDoc.length)
        return {
          ok: false,
          message: `Declared as commands but no <transition event="…"> in the document handles them: ${unhandledInDoc.join(', ')}. A command is an event the statechart reacts to; declare only what the document handles.`,
        };

      const existing = entry.models.get(modelId);

      // The whole model: read, put together, checked and saved as one (Part 5).
      const wiring = rootWiringOf(rawInputs, rawOutputs);
      if (typeof wiring === 'string') return { ok: false, message: wiring };
      const group = groupOf(args.templates, args.children);
      if (typeof group === 'string') return { ok: false, message: group };
      const base: ModelDraft | null = existing && savedSource ? draftOf(savedSource, existing) : emptyDraft();
      if (!base)
        return {
          ok: false,
          message: `"${existing?.name ?? modelId}" is a group, and this relay is older than Part 5 of the Step 3 plan, so it does not send the group's templates. Tell the person it cannot be changed here until the relay is updated.`,
        };
      if (!existing && !name)
        return { ok: false, message: `No model "${modelId}" exists here, so this would create it — but a name is required to do that.` };
      for (const [t, sent] of Object.entries(group.templates ?? {})) {
        const before = base.templates[t]?.scxml ?? '';
        if (before.length > MAX_SCXML_CHARS)
          return { ok: false, message: tooLongToEdit(`${existing?.name ?? modelId}" template "${t}`, before.length), retryable: false };
        const missing = sent ? missingData(sent.scxml, sent.iface.outputs) : [];
        if (missing.length)
          return {
            ok: false,
            message: `Declared as outputs of the template "${t}" but not in its <datamodel>: ${missing.join(', ')}. An output is a <data id="…"> the document assigns; declare only what the document has.`,
          };
      }
      /*
       * THE SLACK MESSAGE AT EACH STATE CHANGE (StateNotifier.ts in the model-runner). The person is
       * stored as they were named, and send_dm finds them among the members of #vitesse-dm at each
       * message. This surface cannot call an MCP tool, so a name that is not on that list shows in the
       * model log at the first change, not here. "" keeps the saved setting, as a model that fills
       * every string sends it for a parameter it does not use.
       */
      const slackTo = typeof args.slackNotifyTo === 'string' ? args.slackNotifyTo.trim() : '';
      const timeVar = typeof args.slackTimeVariable === 'string' ? args.slackTimeVariable.trim() : '';
      if ((slackTo || timeVar) && args.slackNotifyOff === true)
        return { ok: false, message: 'slackNotifyOff stops the Slack messages, so it cannot come with slackNotifyTo or slackTimeVariable: give one or the other.' };
      let notify: DraftNotify | null | undefined;
      if (args.slackNotifyOff === true) notify = null;
      else if (slackTo) {
        const tool = await slackToolOf(deps, typeof args.slackTool === 'string' ? args.slackTool.trim() : '');
        if (!tool.ok) return { ok: false, message: tool.message };
        // The saved time stays unless this write names another.
        const time = timeVar || base.notify?.slackTimeVariable || '';
        notify = { slackTool: tool.tool, slackPerson: slackTo, ...(time ? { slackTimeVariable: time } : {}) };
      } else if (timeVar) {
        if (!base.notify)
          return {
            ok: false,
            message: 'slackTimeVariable is the time of the Slack start and stop lines, and this model sends no Slack messages. Give slackNotifyTo with it.',
          };
        notify = { ...base.notify, slackTimeVariable: timeVar };
      }

      const change: DraftWrite = {
        scxml,
        ...(notify !== undefined ? { notify } : {}),
        ...(declared || declaredOutputs || declaredCommands
          ? {
              iface: {
                ...(declared ? { inputs: declared } : {}),
                ...(declaredOutputs ? { outputs: declaredOutputs } : {}),
                ...(declaredCommands ? { commands: declaredCommands } : {}),
              },
            }
          : {}),
        ...wiring,
        ...group,
      };
      const { draft, parts } = applyWrite(base, change);
      /*
       * THE TIME IS A PORT. The runner sees a model's declared outputs and its wired inputs, never its
       * private datamodel, so a time named anywhere else would fall back to the relay clock in silence.
       */
      if (timeVar) {
        const outs = draft.iface.outputs.map((p) => p.name);
        const ins = draft.iface.inputs.map((p) => p.name);
        if (!outs.includes(timeVar) && !ins.includes(timeVar))
          return {
            ok: false,
            message:
              `"${timeVar}" is neither a declared output nor a declared input of this model, so it cannot be the time of the Slack start and stop lines. ` +
              `Its outputs: ${outs.join(', ') || 'none'}. Its inputs: ${ins.join(', ') || 'none'}. Declare it as an output (in outputs) or name one of these.`,
          };
      }
      if (existing && !savedSource?.descriptor && (parts.includes('inputs') || parts.includes('outputs')))
        return { ok: false, message: OLD_RELAY_WIRING(existing.name) };

      // THE GATE, over connections: only a connection gives an input a value (see THE GATE above).
      const connected = (b: DraftBinding): boolean => 'path' in b && isEntityPath(b.path);
      const live = Object.values(draft.inputs).some(connected) || draft.children.some((c) => Object.values(c.inputs).some(connected));
      const waiting =
        !live &&
        (draft.iface.inputs.length > 0 ||
          draft.children.some((c) => (ownEntry(draft.templates, c.template)?.iface.inputs.length ?? 0) > 0));
      const notYet = waiting ? CONNECTED_TO_NOTHING_YET : '';

      /*
       * THE CHECK of the whole model, as the relay checks a save: every document, the commands, the
       * wiring. It comes before the gate, so a document the relay refuses is named first: one that is
       * not `datamodel="typescript"` among them.
       */
      const checked = await checkDraft(draft, entry.models.all, modelId);
      if (checked && checked.refusingCount > 0)
        return {
          ok: false,
          message:
            `Not sent: the model has ${problems(checked.refusingCount)} that the relay would refuse or that would fail when it runs.` +
            ` Fix every one and write again: this write never reached the relay, so that is not sending the same document twice.\n${checked.refusing}`,
          retryable: false,
        };
      if (!live && !waiting && args.noLiveInputs !== true) return { ok: false, message: NOTHING_CONNECTED };
      const warned = checked?.warnings ? `\n${WRITE_WARNINGS}\n${checked.warnings}` : '';
      const listed = runsAsGroup(draft) || Object.keys(draft.templates).length > 0 ? groupNote(draft) : '';

      // The next write that changes the root gives this, with no read in between (see THE ROOT'S VERSION).
      const rootVersion = await rootVersionOf(draft.scxml);
      const versionNote = ` Its rootVersion is now ${rootVersion}.`;
      const slackNote = !parts.includes('notify')
        ? ''
        : draft.notify
          ? ` Each state change sends ${draft.notify.slackPerson} a Slack message, and so do the model's start and stop, ` +
            (draft.notify.slackTimeVariable ? `with the time from "${draft.notify.slackTimeVariable}".` : 'with the relay clock as the time.') +
            (await armingNote(deps, modelId))
          : ' It sends no Slack messages of state changes any more.';

      try {
        if (existing) {
          const mask = name ? [...parts, 'name' as const] : parts;
          await entry.models.update(modelId, upsertOf(draft, mask, { name: name || existing.name, desiredState: existing.desiredState }));
          return {
            ok: true,
            message:
              `Rewrote "${name || existing.name}" (${mask.join(', ')}).` +
              versionNote +
              slackNote +
              listed +
              notYet +
              // A running model is restarted on an edit: the person hears that from the assistant.
              (existing.status === 'running' ? ' It was running, so it has been restarted with the new definition.' : '') +
              warned,
            data: { modelId, created: false, changed: mask, rootVersion },
          };
        }
        /*
         * THE CREATE VERB, not a whole-descriptor update. A create is gated on `model:create`, and an
         * edit on `model:edit`, so one verb cannot be both: the model-runner refuses a replace-all
         * with nothing to replace. Every part goes in one create, with no mask, because there is
         * nothing to merge into. STOPPED, always: to author a statechart is not a decision to run it
         * against a machine.
         */
        await entry.models.create(modelId, upsertOf(draft, [], { name, desiredState: 'stopped' }));
        // Straight to the statechart when the person watches THIS thread. A tree that works in the
        // background must not take them away from what they read.
        if (ctx?.foreground) askToOpen(`/gateways/${gw.value.id}/models/${modelId}`);
        return {
          ok: true,
          message:
            `Created "${name}". It is STOPPED — tell the user it is ready and let them start it. ` +
            (ctx?.foreground
              ? 'They have been taken to it, so describe what it does rather than telling them where to find it.'
              : `It is at /gateways/${gw.value.id}/models/${modelId} — link it.`) +
            versionNote +
            slackNote +
            listed +
            notYet +
            warned,
          data: { modelId, created: true, rootVersion },
        };
      } catch (e) {
        /*
         * The model-runner's own words. It checks the save and answers with the fault, which lets the
         * next attempt be better than a retry of the same thing, so it is passed through as it is.
         * Not retryable as it is: the same model gets the same refusal.
         */
        return { ok: false, message: `The relay refused that write: ${e instanceof Error ? e.message : String(e)}`, retryable: false };
      }
    },
  };

  /*
   * RENAME — ONE NAME, EVERYWHERE, WITHOUT THE DOCUMENT PASSING THROUGH THE AUTHOR.
   *
   * A rename by models.write means reading the model, finding every place the name is written, and
   * sending the whole document back: a missed `In('High')` is a model that never leaves a state, and
   * a model longer than MAX_SCXML_CHARS cannot be rewritten at all. Here the language service finds
   * every place from the saved source (statechart-host's renameInModel), refuses a rename that would
   * change what the document means, and the author sends two names.
   *
   * STOPPED MODELS ONLY. An edit restarts a running model fresh, but the checkpoint it keeps is under
   * the old names, and a paused model or a relay restart resumes from it: a renamed state is not
   * found, so the model starts over, and a renamed variable comes back empty, with no error. A stop
   * drops the checkpoint (ModelReconciler.ts), so a stopped model is the one safe case.
   *
   * NOT A PUBLISHED NAME. The wiring connects a declared input or output by its name, and other
   * models and boards read an output by its name, so renameInModel refuses those. Only declared outputs are published, so a model that declares
   * none has no published variable, and each of its variables can be renamed.
   *
   * THE SAME CHECK AS A WRITE, on what changed. A model saved before the check existed may already
   * have problems a write would refuse; the service keeps its own findings the same across a rename,
   * so only a problem the rename added refuses it, and the old ones are left to models.write.
   */
  const rename: ClientTool = {
    name: 'models.rename',
    // Models/GetModelSource, then Models/Update.
    permission: Permission.MODEL_EDIT,
    description:
      'Rename one state, or one variable of the root <datamodel>, everywhere a saved model names it: its id, every target, ' +
      'initial, done.state event and In() for a state; its <data> and every use in an expression for a variable. ' +
      'You send only the two names, not the document, so it works on a model of any length. Use it, not models.write, to ' +
      'rename. Only a STOPPED model is renamed. Declared inputs and outputs are never renamed: the wiring, other models and ' +
      'boards name them.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The model to change. It must exist and be stopped.' },
        from: { type: 'string', description: 'The state id or variable name as the document writes it now.' },
        to: { type: 'string', description: 'The new name.' },
        kind: {
          type: 'string',
          enum: ['state', 'variable'],
          description: 'Which one, only when a state and a variable have the same name.',
        },
      },
      required: ['modelId', 'from', 'to'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '').trim();
      const from = String(args.from ?? '').trim();
      const to = String(args.to ?? '').trim();
      const kind = args.kind === 'state' || args.kind === 'variable' ? args.kind : undefined;
      if (!from || !to) return { ok: false, message: 'Both names are required: `from`, as the document writes it now, and `to`.' };
      const view = entry.models.get(modelId);
      if (!view) {
        const known = entry.models.all.map((m) => m.id);
        return {
          ok: false,
          message: `No model "${modelId}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ''}`,
        };
      }
      if (view.desiredState !== 'stopped' || view.status === 'paused')
        return { ok: false, message: notStoppedForRename(view.name, view.status), retryable: false };

      let saved: string;
      // The saved model's profile: a hard model's rename is checked with the hard rules, as its save is.
      let profile: ReturnType<typeof profileOf>;
      try {
        const source = await entry.models.getSource(modelId);
        saved = source.scxml;
        profile = profileOf(source.descriptor);
      } catch (e) {
        return {
          ok: false,
          message: `Could not read the source of "${view.name}": ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      const options = {
        inputs: view.inputs ?? [],
        outputs: view.outputs ?? [],
        commands: view.commands ?? [],
        types: portTypes(view.iface),
        ...profile,
      };
      let renameInModel: typeof import('@vitesse/statechart-host').renameInModel;
      try {
        ({ renameInModel } = await import('@vitesse/statechart-host'));
      } catch (e) {
        return {
          ok: false,
          message: `Could not load the SCXML language service to rename: ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      // A `typescript` document is read as the engine reads it; the edits land in the saved text, which has the same positions.
      const result = renameInModel(saved, { ...options, strip: await stripForEngine(saved) }, { from, to, ...(kind ? { kind } : {}) });
      if ('refused' in result) return { ok: false, message: `Not renamed: ${result.refused}.`, retryable: false };

      const after = await checkDocument(result.scxml, options);
      let unchanged = 0;
      if (after && after.refusing.length > 0) {
        const before = await checkDocument(saved, options);
        const added = newFindings(before?.refusing ?? [], after.refusing);
        if (added.length > 0)
          return {
            ok: false,
            message: refusalMessage(
              `Not renamed: the renamed document would have ${problems(added.length)} the saved one does not. Nothing was written.`,
              result.scxml,
              added,
            ),
            retryable: false,
          };
        unchanged = after.refusing.length;
      }

      try {
        await entry.models.update(modelId, {
          name: view.name,
          scxml: result.scxml,
          desiredState: view.desiredState,
          // The document alone: the wiring and the declared interface do not change with a rename.
          updateMask: ['scxml'],
        });
      } catch (e) {
        return {
          ok: false,
          message: `The relay refused the renamed document: ${e instanceof Error ? e.message : String(e)}`,
          retryable: false,
        };
      }
      return {
        ok: true,
        message:
          `Renamed ${result.kind} "${from}" to "${to}" in "${view.name}", in ${result.places} place${result.places === 1 ? '' : 's'}` +
          ` (${renamedLines(result.lines)}).` +
          (result.kind === 'state'
            ? ` Anything outside the model that compares its state with "${from}" by name, such as a custom widget, still uses the old name, and this tool cannot find it: say so to the person.`
            : '') +
          (unchanged > 0
            ? ` The saved model already had ${problems(unchanged)} that models.write would refuse; the rename did not change them, and models.read lists them.`
            : ''),
        data: { modelId, kind: result.kind, from, to, places: result.places, lines: result.lines },
      };
    },
  };

  /*
   * DELETE — the one call here that cannot be undone.
   *
   * `model:delete` is its own permission, so an operator who may author a statechart does not
   * automatically hold the power to remove one; the api-gateway decides that against the user's own
   * session, as with every other tool.
   *
   * IT ASKS FOR THE NAME AS WELL AS THE ID, and that is not ceremony. Every other tool here fails
   * safely on a wrong id — the read says "no such model", the write creates one the user can see
   * and remove. A wrong id HERE destroys a statechart that may be driving a machine, and the model
   * choosing it is working from a list it fetched some turns ago. Requiring the name means an id
   * that has drifted is caught by a mismatch rather than by the consequence.
   *
   * Deleting a RUNNING model is allowed: refusing would make a misbehaving automaton the one thing
   * a person cannot get rid of. The outcome says it was running, so the assistant can say so too.
   */
  /*
   * THE VERB THAT CONNECTS A MODEL, which was missing once, and its absence read as a refusal.
   *
   * An authored model is inert until its inputs are connected to real symbols. When `models.write`
   * could not connect them, a specialist wrote a correct statechart, could not wire it, and said
   * so. Read back, the model looked half-finished, and the honest answer ("I cannot do that") read
   * like a problem of permissions.
   *
   * So a connection has its OWN verb. It writes the root's own wiring (`inputs`), masked to that
   * alone, as `path` in the inputs of models.write does: the model reads each input by its name,
   * and only the wiring gives that name a value. It cannot touch the document, so neither call can
   * silently undo the other's work. The name stays `models.bind`, because the roles give it by
   * that name.
   */
  const bind: ClientTool = {
    name: 'models.bind',
    description:
      "Connect a model's declared inputs to real symbols, which is what makes an authored model run against a machine. " +
      'The model reads each input by its name, and only a connection gives it a value: a model whose inputs are not ' +
      'connected sits in its initial state whatever the plant does. Send the complete set of paths you mean the model to ' +
      'have: they replace the paths there, and a constant set through models.write stays. Use `models.read` first to see ' +
      'the inputs it declares and what each is connected to. This never changes the SCXML.',
    mutates: true,
    permission: Permission.MODEL_EDIT,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The model to connect. It must already exist.' },
        inputs: {
          type: 'object',
          description:
            'Input name to symbol path, e.g. {"Stacklight": "controllers/core/symbols/Stacklight"}. Replaces the paths ' +
            'the model has, so include the ones you want to keep. An empty object disconnects every input that has a path.',
          additionalProperties: { type: 'string' },
        },
      },
      required: ['modelId', 'inputs'],
      additionalProperties: false,
    },
    async run(args): Promise<ToolOutcome> {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '').trim();
      if (!modelId) return { ok: false, message: 'A modelId is required — bind names an existing model.' };

      const existing = entry.models.get(modelId);
      // A connection is not a way to CREATE. A missing model here means the id is wrong, and an empty
      // model made to hold the connections would leave something nobody asked for on the relay.
      if (!existing) return { ok: false, message: `No model "${modelId}" on this relay. models.list shows what is here.` };

      const raw = (args.inputs ?? {}) as Record<string, unknown>;
      const given: Record<string, string> = {};
      for (const [k, v] of Object.entries(raw)) {
        if (typeof v !== 'string') return { ok: false, message: `The path for "${k}" must be a symbol path, as a string.` };
        /*
         * A PATH, OR NOTHING. models.bind connects only a path that starts with `controllers/` or
         * `models/`. A bare symbol name is no path, so a connection to it is no connection, on a
         * model that then looks connected. That is the failure this refuses: an input connected to
         * "Stacklight" instead of "controllers/core/symbols/Stacklight" left a statechart that
         * watched a symbol it could never hear, while the Bindings panel showed the wiring as done.
         */
        if (!isEntityPath(v))
          return {
            ok: false,
            message:
              `"${v}" is not an entity path that models.bind can connect, so "${k}" stays unconnected: models.bind connects ` +
              'only paths that start with controllers/ or models/. Use the full path, as symbols.search returns it: ' +
              'controllers/<controller-id>/symbols/<symbol-path> for a PLC symbol, or models/<model-id>/variables/<name> ' +
              "for another model's output.",
          };
        given[k] = v.trim();
      }

      // The whole wiring, from the same read as the descriptor: the mask replaces the whole map.
      let source: Awaited<ReturnType<typeof entry.models.getSource>>;
      try {
        source = await entry.models.getSource(modelId);
      } catch (e) {
        return {
          ok: false,
          message: `Could not read "${existing.name}" to keep its other connections: ${e instanceof Error ? e.message : String(e)}`,
          retryable: true,
        };
      }
      const base = draftOf(source, existing);
      if (!base)
        return {
          ok: false,
          message: `"${existing.name}" is a group, and this relay is older than Part 5 of the Step 3 plan, so its wiring cannot be read here. Tell the person it cannot be connected here until the relay is updated.`,
        };
      // The set given replaces the paths there. A constant stays: models.write sets it.
      const inputs: Record<string, DraftBinding | null> = {};
      for (const [port, b] of Object.entries(base.inputs)) if ('path' in b && !Object.hasOwn(given, port)) inputs[port] = null;
      for (const [port, path] of Object.entries(given)) inputs[port] = { path };
      const { draft, parts } = applyWrite(base, { scxml: base.scxml, inputs });
      const mask = parts.filter((p) => p !== 'scxml');
      if (!source.descriptor && mask.length > 0) return { ok: false, message: OLD_RELAY_WIRING(existing.name) };
      const n = Object.keys(given).length;
      try {
        if (mask.length > 0)
          await entry.models.update(modelId, upsertOf(draft, mask, { name: existing.name, desiredState: existing.desiredState }));
        return {
          ok: true,
          message:
            (n
              ? `Connected ${n} input${n === 1 ? '' : 's'} on "${existing.name}", which reads each by its name. Its SCXML is unchanged.`
              : `Disconnected the inputs of "${existing.name}". It will receive no values until they are connected again.`) +
            (existing.status === 'running' && mask.length > 0 ? ' It was running, so it has been restarted with the new connections.' : ''),
          data: { modelId, inputs: given, changed: mask },
        };
      } catch (e) {
        return { ok: false, message: `Could not connect "${modelId}": ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  const del: ClientTool = {
    name: 'models.delete',
    // Models/Delete.
    permission: Permission.MODEL_DELETE,
    description:
      'Permanently delete a state machine from the open relay. This stops it if it is running and cannot be undone. ' +
      'You must pass both the modelId and the exact current name, which you should read with models.list first — ' +
      'if they do not match, nothing is deleted. Ask the user before calling this.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The id of the model to delete.' },
        name: { type: 'string', description: "That model's exact current name, as confirmation." },
      },
      required: ['modelId', 'name'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '').trim();
      const claimed = String(args.name ?? '').trim();
      const view = entry.models.get(modelId);
      if (!view) {
        const known = entry.models.all.map((m) => `${m.id} ("${m.name}")`);
        return {
          ok: false,
          message: `No model "${modelId}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ''}`,
        };
      }
      if (claimed !== view.name) {
        // The mismatch IS the safety property — say which one it actually is rather than just "no".
        return {
          ok: false,
          message: `Not deleted: "${modelId}" is named "${view.name}", not "${claimed}". Check you have the right model, then call again with its real name.`,
        };
      }
      const wasRunning = view.status === 'running';
      try {
        await entry.models.delete(modelId);
        return {
          ok: true,
          message: `Deleted "${view.name}".${wasRunning ? ' It was running and has been stopped.' : ''}`,
          data: { modelId, wasRunning },
        };
      } catch (e) {
        return { ok: false, message: `The relay refused that delete: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  /*
   * RUNNING A MODEL — start, stop, and the commands it declares.
   *
   * One tool rather than three, because the platform has one verb: `ExecuteCommand` takes the five
   * reserved lifecycle names (start, stop, pause, resume, reset) and each model's own declared
   * commands through the same door. Splitting them here would invent a distinction the contract
   * does not make, and would leave the declared commands — the interesting half — unreachable.
   *
   * IT REFUSES A COMMAND THE MODEL DOES NOT DECLARE, and names what it does declare. The model
   * chooses from a list it read some turns ago, so a guess is the common failure and a list is the
   * fix it can act on. Reserved names are always allowed: every model has a lifecycle.
   */
  const command: ClientTool = {
    name: 'models.command',
    // Models/ExecuteCommand.
    permission: Permission.MODEL_EXECUTE,
    description:
      'Start, stop, pause, resume or reset a state machine, or send it one of the commands it declares. ' +
      'Starting a model makes it act on the machine, so say what it does and let the user decide unless they have already asked for it. ' +
      'Use models.read first to see which commands a model declares.',
    mutates: true,
    parameters: {
      type: 'object',
      properties: {
        gateway: GATEWAY_PARAM,
        modelId: { type: 'string', description: 'The model id.' },
        command: { type: 'string', description: "start | stop | pause | resume | reset, or one of the model's declared commands." },
        data: {
          type: 'object',
          description: 'Optional parameters for a declared command, as string values.',
          additionalProperties: { type: 'string' },
        },
      },
      required: ['modelId', 'command'],
      additionalProperties: false,
    },
    run: async (args) => {
      const gw = resolveGateway(deps, args.gateway);
      if (!gw.ok) return { ok: false, message: gw.message };
      const entry = gw.value.entry;
      const modelId = String(args.modelId ?? '').trim();
      const cmd = String(args.command ?? '').trim();
      const view = entry.models.get(modelId);
      if (!view) {
        const known = entry.models.all.map((m) => m.id);
        return {
          ok: false,
          message: `No model "${modelId}" on this relay.${known.length ? ` The models here are: ${known.join(', ')}.` : ''}`,
        };
      }
      if (!cmd)
        return {
          ok: false,
          message: 'A command is required — one of start, stop, pause, resume, reset, or a command this model declares.',
        };
      if (!LIFECYCLE.has(cmd) && !view.commands.includes(cmd)) {
        return {
          ok: false,
          message:
            `"${view.name}" does not declare a command called "${cmd}". ` +
            `It accepts ${[...LIFECYCLE].join(', ')}` +
            `${view.commands.length ? `, and its own: ${view.commands.join(', ')}` : ' and declares no commands of its own'}.`,
        };
      }
      // Reading the state BEFORE, so the outcome can say what actually changed rather than what was
      // asked for — "it was already stopped" is a different answer from "stopped it".
      const before = view.status;
      try {
        const data = typeof args.data === 'object' && args.data ? (args.data as Record<string, string>) : undefined;
        if (cmd === 'start') await entry.models.startModel(modelId);
        else if (cmd === 'stop') await entry.models.stopModel(modelId);
        else await entry.models.execute(modelId, cmd, data);
        return {
          ok: true,
          message:
            cmd === 'start'
              ? `Started "${view.name}"${before === 'running' ? ' (it was already running)' : ''}. It is now acting on the machine.`
              : cmd === 'stop'
                ? `Stopped "${view.name}"${before !== 'running' ? ` (it was ${before})` : ''}.`
                : `Sent "${cmd}" to "${view.name}".`,
          data: { modelId, command: cmd, statusBefore: before },
        };
      } catch (e) {
        return { ok: false, message: `The relay refused that command: ${e instanceof Error ? e.message : String(e)}` };
      }
    },
  };

  return registerClientTools(list, read, write, rename, bind, command, del);
}
